import { Component, inject, ChangeDetectionStrategy, computed } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import { ConfigService } from '@fcom/core/services/config/config.service';
import { LanguageService } from '@fcom/ui-translate';
import { loginStatusSelector } from '@fcom/core/selectors/login.selector';
import { LoginStatus } from '@fcom/core-api/login/interfaces/login.interface';
import { BookingAppState } from '@fcom/common/interfaces/booking/booking-state.interface';
import { FragmentService } from '@fcom/common/services/fragments/fragment.service';
import { CmsContent } from '@fcom/core-api/interfaces/cms-interface';
import { PromoCardCtaData } from '@fcom/ui-components/components/promo-card/promo-card.component';
import { PromoTheme } from '@fcom/ui-components/components/promo-card/enum';
import { ButtonTheme } from '@fcom/ui-components/components/buttons/button/enums';

@Component({
  selector: 'fin-promotion-banners',
  templateUrl: './promotion-banners.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PromotionBannersComponent {
  private configService = inject(ConfigService);
  private languageService = inject(LanguageService);
  private store$ = inject<Store<BookingAppState>>(Store);
  private fragmentService = inject(FragmentService);

  readonly LoginStatus = LoginStatus;
  readonly PromoTheme = PromoTheme;

  readonly loggedIn = this.store$.selectSignal(loginStatusSelector);

  // Logged in - Donate avios banner
  readonly donateTexts = this.languageService.translateMultipleSignal<[string, string, string]>([
    'donatePoints.title',
    'donatePoints.text',
    'donatePoints.button',
  ]);
  readonly donateAviosImage = rxResource({
    params: () => (this.loggedIn() === LoginStatus.LOGGED_IN ? 'fragments.donatePoints' : undefined),
    stream: ({ params }) => this.fragmentService.getCmsPictureFromFragment(params),
  });

  private readonly finnairShopDonateUrl = this.buildDonateUrl();

  readonly donateAviosData = computed<Partial<CmsContent>>(() => {
    const [title, teaserText] = this.donateTexts() ?? [];
    return { title, teaserText };
  });
  readonly donateAviosCta = computed<PromoCardCtaData>(() => {
    const [, , buttonTitle] = this.donateTexts() ?? [];
    return {
      title: buttonTitle ?? '',
      url: this.finnairShopDonateUrl,
      external: true,
      openInNewTab: true,
      theme: ButtonTheme.SECONDARY_OUTLINE,
    };
  });

  // Logged out - Sustainable Travel banner
  readonly sustainableTravelTexts = this.languageService.translateMultipleSignal<[string, string, string, string]>([
    'sustainableTravel.title',
    'sustainableTravel.text',
    'sustainableTravel.button',
    'sustainableTravel.link.url',
  ]);
  readonly sustainableTravelImage = rxResource({
    params: () => (this.loggedIn() === LoginStatus.NOT_LOGGED_IN ? 'fragments.sustainableTravel' : undefined),
    stream: ({ params }) => this.fragmentService.getCmsPictureFromFragment(params),
  });

  readonly sustainableTravelData = computed<Partial<CmsContent>>(() => {
    const [title, teaserText] = this.sustainableTravelTexts() ?? [];
    return { title, teaserText };
  });
  readonly sustainableTravelCta = computed<PromoCardCtaData>(() => {
    const [, , buttonTitle, url] = this.sustainableTravelTexts() ?? [];
    return {
      title: buttonTitle ?? '',
      url: url ?? '',
      external: true,
      openInNewTab: true,
      theme: ButtonTheme.SECONDARY_OUTLINE,
    };
  });

  private buildDonateUrl(): string {
    let lang = this.languageService.langKeyValue?.toLowerCase();

    // Only EN FI SV languages are supported by Chooose.
    if (!['en', 'fi', 'sv'].includes(lang)) {
      lang = 'en';
    }

    return this.configService.cfg.finnairShopDonateUrl.replace('{lang}', lang);
  }
}
